import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDomainConfig } from "../../config/config";
import { useCart } from "../../hooks/useCart";
import { getTicketTitle } from "../../utils/cart.js";
import { AttributeField } from "../Input/Input";
import StandardFieldset from "../UI/StandardFieldset";
import StandardH2 from "../UI/StandardH2";
import StandardH3 from "../UI/StandardH3";
import StandardH4 from "../UI/StandardH4";
import StandardH5 from "../UI/StandardH5";
import ReactHtmlParser from 'react-html-parser'
import { googleDataLayer } from "../Analytics/Universal/PageView/googleDataLayer";
import { ABContext } from "../../contexts/ab-context";
import ComboDetails from "./ComboDetails";

const TicketDetails = (props) => {
  const config = getDomainConfig();

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);
  const { t } = useTranslation('common');
  const { guestInfo, setGuestInfo, setValue, isMainGuestChecked, trigger } = props;

  useEffect(() => {

    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("TicketDetails", ".module.scss");

  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  const abContext = useContext(ABContext);

  // CRO-1381-checkout-redesign
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  const { isSuccess: cartIsSuccess, data: cartContents } = useCart();

  let byProducts = {};
  let cartTickets = [];
  let comboProducts = {};
  if (cartIsSuccess) {
    cartTickets = cartContents.data.tickets;

    // Loop through tickets and get attributes.
    cartTickets.forEach((ticket) => {
      // Exclude date/time attributes as these can't be modified on the checkout page:
      let checkoutAttributes = ticket.internal.attributes.filter(
        (a) => ["5", "10"].indexOf(a.attribute_id.split("-")[2]) === -1
      );
      // Exclude any backend only attributes.
      checkoutAttributes = checkoutAttributes.filter(
        (a) => a.backend_only === "0"
      );
      // Exclude any extension attribute select_dynamic attribute
      checkoutAttributes = checkoutAttributes.filter(
        (a) => !(a.type === "select_dynamic")
      );
      // Display select dynamic attributes
      const selectDynamicAttributes = ticket.internal.attributes.filter(
        (a) => a.type === "select_dynamic"
      );
      // Exclude any "hidden" attributes.
      checkoutAttributes = checkoutAttributes.filter(
        (a) => a.type !== "hidden"
      );
      // Exclude any hotel related attributes.
      checkoutAttributes = checkoutAttributes.filter(
        (a) =>
          ["42", "48", "49", "87"].indexOf(a.attribute_id.split("-")[2]) === -1
      );

      if (checkoutAttributes.length) {
        if (!(ticket.product_id in byProducts) && !ticket.tags.includes('combo')) {
            byProducts[ticket.product_id] = {};
            // Add date/time to product title, where applicable:
            byProducts[ticket.product_id] = {
              title: getTicketTitle(ticket, false),
              tickets: [],
            };
        } else if (!(ticket.date in comboProducts) && ticket.tags.includes('combo')) {
            // Create comboProduct with product id to separate normal tickets form combo
            comboProducts[ticket.date] = {};
            // Add date/time to product title, where applicable:
            comboProducts[ticket.date] = {
              title: getTicketTitle(ticket, false),
              tickets: [],
            };
        }

        // Get attributes.
        const attributesPerTicket = checkoutAttributes.filter(
          (a) => a.per_qty !== "1"
        );
        const attributesPerPerson = checkoutAttributes.filter(
          (a) => a.per_qty === "1"
        );
        const ticketAttributes = [];
        // Attributes per ticket.
        if (attributesPerTicket.length > 0) {
          const groupedAttributes = [];
          attributesPerTicket.forEach((attribute) => {
            attribute.key =
              "attr_" +
              ticket.cart_item_id +
              "_" +
              attribute.attribute_id.replaceAll("-", "_");
            groupedAttributes.push(attribute);
          });
          ticketAttributes.push(groupedAttributes);
        }

        // Attributes per person (need to loop ticket qty).
        if (attributesPerPerson.length > 0) {
          for (let n = 0; n < ticket.qty; n += 1) {
            const groupedAttributes = [];
            attributesPerPerson.forEach((attribute) => {
              attribute.key =
                "attr_" +
                ticket.cart_item_id +
                "_" +
                attribute.attribute_id.replaceAll("-", "_");
              groupedAttributes.push(attribute);
            });
            ticketAttributes.push(groupedAttributes);
          }
        }

        if (ticket.tags.includes('combo')) {
          // Combine all combo tickets to separate object for further data manipulation in ComboDetails component
          comboProducts[ticket.date].tickets.push({
            ticketId: ticket.ticket_id,
            cartItemId: ticket.cart_item_id,
            qty: parseInt(ticket.qty, 10),
            type: ticket.internal.type,
            tags: typeof ticket.tags !== undefined ? ticket.tags : [],
            attributes: ticketAttributes,
            rawAttributes: ticket.attributes,
            selectDynamicAttributes: selectDynamicAttributes,
          });
        } else {
          // Combine all the tickets and attributes.
          byProducts[ticket.product_id].tickets.push({
            ticketId: ticket.ticket_id,
            cartItemId: ticket.cart_item_id,
            qty: parseInt(ticket.qty, 10),
            type: ticket.internal.type,
            tags: typeof ticket.tags !== undefined ? ticket.tags : [],
            attributes: ticketAttributes,
            rawAttributes: ticket.attributes,
            selectDynamicAttributes: selectDynamicAttributes,
          });
        }
      }
    });
  }

  // Checkbox functionality for main guest

  // Helper function to determine the type of attribute based on title for tickets only
  const determineAttributeType = (attribute_id, title) => {
    const id = parseInt(attribute_id.split("-")[2], 10);
    switch (id) {
      case 13:
      case 38:
      case 123:
      case 52:
        return title.toLowerCase().includes('name') ? 'fullName' : null;
      case 19:
        return 'firstName';
      case 20:
        return 'lastName';
      case 206:
        return null;
      default:
        return title.toLowerCase().includes('name') ? 'fullName' : null;
    }
  };

  // Function to set the attribute values based on type
  const setAttributeValue = (key, type, guestInfo, shouldValidate = false) => {

    // This will check if there are values in the fields which should be populated to avoid overwriting fields after user correct them, if so, skip it.
    if(props.getValues(`${key}_0`)) return;

    const options = shouldValidate ? { shouldDirty: true, shouldTouch: true } : {};
    switch (type) {
      case 'fullName':
        const index = (document.getElementsByName(key + '_0').length === 0) ? '_1' : '_0';

        setValue(key + index, `${guestInfo.firstName} ${guestInfo.lastName}`, options);
        break;
      case 'firstName':
        setValue(key + '_0', guestInfo.firstName, options);
        break;
      case 'lastName':
        setValue(key + '_0', guestInfo.lastName, options);
        break;
      default:
        // No default action
        break;
    }

    if (shouldValidate) {
      trigger(key + '_0');
    }
  };

  useEffect(() => {
    if (!guestInfo || !cartIsSuccess || !isMainGuestChecked) return;

    const fieldsToValidate = [];
    cartTickets.forEach(ticket => {

      if (
        !ticket.internal.type.toLowerCase().includes('child') &&
        !ticket.internal.type.toLowerCase().includes('kind') &&
        !ticket.internal.type.toLowerCase().includes('infant') &&
        !ticket.internal.type.toLowerCase().includes('baby')
      ) {
        ticket.internal.attributes.forEach(attribute => {
          const attributeKey = `attr_${ticket.cart_item_id}_${attribute.attribute_id.replaceAll("-", "_")}`;
          const attributeType = determineAttributeType(attribute.attribute_id, attribute.title);

          if (attributeType) {
            setAttributeValue(attributeKey, attributeType, guestInfo, true);
            fieldsToValidate.push(attributeKey + '_0');
          }
        });
      }
    });

    // Trigger validation for all updated fields
    trigger(fieldsToValidate);

  }, [guestInfo, setValue, cartTickets, cartIsSuccess, isMainGuestChecked, trigger]);

  // This is purely for hotel functionality as the attributes are too different to tickets
  useEffect(() => {
    if (!guestInfo || !cartIsSuccess || !isMainGuestChecked) return;

    const fieldsToValidate = [];

    cartTickets.forEach(ticket => {
      if (ticket.tags.includes("hotels")) { // Check if the ticket is related to hotels

        ticket.internal.attributes.forEach(attr => {

          if (attr.title.includes("Guest list")) {

            let baseName = `attr_${ticket.cart_item_id}_${attr.attribute_id}`;
            baseName = baseName.replace(/-/g, '_');

            setValue(`${baseName}_guest_name_adult_0_0`, guestInfo.firstName, { shouldDirty: true, shouldTouch: true });
            setValue(`${baseName}_guest_name_adult_0_0_1`, guestInfo.lastName, { shouldDirty: true, shouldTouch: true });
            fieldsToValidate.push(`${baseName}_guest_name_adult_0_0`);
            fieldsToValidate.push(`${baseName}_guest_name_adult_0_0_1`);
          }
        });
      }
    });
    // Trigger validation for all updated fields
    trigger(fieldsToValidate);
  }, [guestInfo, cartIsSuccess, cartTickets, setValue, isMainGuestChecked, trigger]);

  if (Object.keys(byProducts).length === 0 && Object.keys(comboProducts).length === 0) {

    return "";
  }

  let ticketDetailsTitle = 'ticket-details'

  if (cartContents.meta.is_hotel_cart) {
    ticketDetailsTitle = 'booking-details';
  }

  return (
    <StandardFieldset id="checkout-attributes">
      <StandardH2 className={isCro1381 && ("cro1381H2")}>{t(ticketDetailsTitle)}</StandardH2>
        {!isCro1381 &&
        <StandardH3>{t('who-will-use-these-tickets')}</StandardH3>
        }
        <div id="checkout-attributes-items" className={styles.tickets}>
          {Object.getOwnPropertyNames(byProducts).map((productId) => (
            <React.Fragment key={productId}>
              <StandardH4 className={!isCro1381 && "fancy"} dataTestId='ticket-title'>
                {ReactHtmlParser(byProducts[productId].title)}
              </StandardH4>
              {byProducts[productId].tickets.map((ticket,indexPrime) => (
                <>
                  <div>
                    {ticket.selectDynamicAttributes.map((attribute) => (
                      attribute.options?.some(option => option.display) && (
                        <StandardH5>
                          {attribute.title} - {attribute.options.filter(option => option.display).map(option => option.display).join(', ')}
                        </StandardH5>
                      )
                    ))}
                  </div>
                  <div className={styles.people} key={ticket.ticketId}>
                    {byProducts[productId].tickets.length > 1 && (
                      <StandardH5 className={styles.person}>
                        {t(ticket.type)}
                      </StandardH5>
                    )}
                    <div>
                      {ticket.attributes.map((attribute, index) => (
                        <>
                          {/* Moving this section (h5) above so it doesn't mess with 2-col layout  */}
                          {ticket.attributes.length > 1 && isCro1381 && (
                            <StandardH5 className={styles.type}>
                              {attribute.map((att, index2) => {
                                if (byProducts[productId].tickets[indexPrime].attributes[index2] && byProducts[productId].tickets[indexPrime].attributes[index2].some(att => att.per_qty !== '1') && index !== 0) {
                                  return (ticket.type ? t(ticket.type) : 'Ticket') + " " + (index);
                                } else if (+att.per_qty === 1 && index2 === 0) {
                                  return (ticket.type ? t(ticket.type) : 'Ticket') + " " + (index + 1);
                                }
                                return '';
                              })}
                            </StandardH5>
                          )}
                          <div className={isCro1381 && attribute.some(att => att.title.includes(t("first-identifier")) || att.title.includes(t("last-identifier"))) ? // Check to see if field contains First or Last as we serve up a different layout
                            "cro1381HalfForm" :
                            isCro1381 && ticket.tags.includes('hotels') ? "cro1381FullForm" :
                              isCro1381 && ticket.attributes.length > 1 ? "cro1381FullForm" : ""}>
                            <React.Fragment key={ticket.ticketId + "_" + index}>
                              {/* Original h5 placement with !isCro177 conditional  */}
                              {ticket.attributes.length > 1 && !isCro1381 && (

                                <StandardH5 className={styles.type}>
                                  {attribute.map((att, index2) => {
                                    if (byProducts[productId].tickets[indexPrime].attributes[index2] && byProducts[productId].tickets[indexPrime].attributes[index2].some(att => att.per_qty !== '1') && index !== 0) {
                                      return (ticket.type ? t(ticket.type) : 'Ticket') + " " + (index);
                                    } else if (+att.per_qty === 1 && index2 === 0) {
                                      return (ticket.type ? t(ticket.type) : 'Ticket') + " " + (index + 1);
                                    }
                                    return '';
                                  })}
                                </StandardH5>
                              )}
                              {attribute.map((att, index2) => (
                                <React.Fragment key={att.key + "_" + index}>
                                  <AttributeField
                                    props={props}
                                    att={att}
                                    index={index}
                                    ticket={ticket}
                                    attTitle={att.title}
                                    product={byProducts[productId].title}
                                    fieldTitle={(ticket.type + ' ' + (index + 1))}
                                    maskingClass="gb-mask"
                                    dataLayerInput={() => googleDataLayer.createTicketDetailsFieldsEvent(att.title, byProducts[productId].title, (ticket.type + ' ' + (index + 1)))}
                                    isHotelCart={cartContents.meta.is_hotel_cart}
                                    setGuestInfo={setGuestInfo}
                                    />
                                </React.Fragment>
                              ))}
                            </React.Fragment>
                          </div>
                        </>
                      ))}
                    </div>
                  </div>
                </>
              ))}

            </React.Fragment>
          ))}
          {comboProducts && <ComboDetails config={config} comboProducts={comboProducts} isHotelCart={cartContents.meta.is_hotel_cart} passedProps={props} isCro1381={isCro1381}/>}
        </div>
    </StandardFieldset>
  );
};

export default TicketDetails;
